import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import './AdminStatisticsPage.css';
import { translateError } from '../../utils/errorMessages';

interface AdminStats {
  adminId: string;
  adminName: string;
  monthlyHours: { [month: number]: number };
  totalHours: number;
}

interface StatisticsResponse {
  year: number;
  admins: AdminStats[];
}

const MONTH_KEYS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
];

const AdminStatisticsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  const [years, setYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [statistics, setStatistics] = useState<AdminStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Permission check
  const canView = hasPermission('statistics:read');

  const fetchYears = useCallback(async () => {
    try {
      const { data } = await apiClient.get<number[]>('/api/statistics/years');
      setYears(data);
      if (data.length > 0 && !data.includes(selectedYear)) {
        setSelectedYear(data[0]);
      }
    } catch (err: any) {
      // Silently fail for years fetch
    }
  }, [selectedYear]);

  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await apiClient.get<StatisticsResponse>(`/api/statistics/admins/${selectedYear}`);
      setStatistics(data.admins);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (canView) {
      fetchYears();
    }
  }, [fetchYears, canView]);

  useEffect(() => {
    if (canView) {
      fetchStatistics();
    }
  }, [fetchStatistics, canView]);

  const formatHours = (hours: number | undefined): string => {
    if (!hours || hours === 0) return '-';
    // Convert decimal hours to HH:MM format
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  // Calculate totals for each month
  const monthlyTotals = MONTH_KEYS.map((_, index) => {
    return statistics.reduce((sum, admin) => sum + (admin.monthlyHours[index] || 0), 0);
  });

  const grandTotal = statistics.reduce((sum, admin) => sum + admin.totalHours, 0);

  if (!canView) {
    return (
      <div className="statistics-page">
        <PageHeader title={t('statistics.title')} helpKey="help.admin.statistics" />
        <p className="error-message">{t('common.noPermission')}</p>
      </div>
    );
  }

  if (loading && statistics.length === 0) {
    return <div className="statistics-loading">{t('statistics.loading')}</div>;
  }

  return (
    <div className="statistics-page">
      <PageHeader
        title={t('statistics.title')}
        helpKey="help.admin.statistics"
        action={
          <div className="statistics-controls">
            <label htmlFor="year-select">{t('statistics.year')}:</label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              disabled={loading}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        }
      />

      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      <div className="statistics-table-container">
        <table className="statistics-table">
          <thead>
            <tr>
              <th className="admin-column">{t('statistics.admin')}</th>
              {MONTH_KEYS.map((monthKey, index) => (
                <th key={index} className="month-column">{t(`statistics.monthsShort.${monthKey}`)}</th>
              ))}
              <th className="total-column">{t('statistics.total')}</th>
            </tr>
          </thead>
          <tbody>
            {statistics.length === 0 ? (
              <tr>
                <td colSpan={14} className="no-data">
                  {t('statistics.noData')}
                </td>
              </tr>
            ) : (
              <>
                {statistics.map((admin) => (
                  <tr key={admin.adminId}>
                    <td className="admin-column">{admin.adminName}</td>
                    {MONTH_KEYS.map((_, index) => (
                      <td key={index} className="month-column">
                        {formatHours(admin.monthlyHours[index])}
                      </td>
                    ))}
                    <td className="total-column total-value">
                      {formatHours(admin.totalHours)}
                    </td>
                  </tr>
                ))}
                <tr className="totals-row">
                  <td className="admin-column">{t('statistics.totalRow')}</td>
                  {monthlyTotals.map((total, index) => (
                    <td key={index} className="month-column total-value">
                      {formatHours(total)}
                    </td>
                  ))}
                  <td className="total-column grand-total">
                    {formatHours(grandTotal)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <p className="statistics-note">{t('statistics.hoursNote')}</p>
    </div>
  );
};

export default AdminStatisticsPage;
